import { GridColDef } from '@mui/x-data-grid'
import { Dispatch, SetStateAction, useMemo } from 'react'
import { OrdersRowModel } from './types'
import { format } from 'date-fns'
import { Chip, Stack, SvgIcon, Tooltip, Typography } from '@mui/material'
import { IconAlertCircle } from '@tabler/icons-react'
import { TextLink } from '~/ui/components/TextLink'
import { CopyToClipboardIconButton } from '~/ui/components/CopyToClipboardIconButton'
import { createShopifyOrderUrl } from '../shopify/utils'
import { OrderListMenu } from './components/OrderListMenu'
import { editVisitorPath } from '../routes'

export const useOrderListColumns = ({
  setVisitorCandidate,
}: {
  setVisitorCandidate: Dispatch<SetStateAction<string | null>>
}) => {
  const columns = useMemo<GridColDef<OrdersRowModel>[]>(
    () => [
      {
        field: 'createdAt',
        headerName: 'Created at',
        valueFormatter: (value: string) => format(value, 'dd MMM yy'),
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 150,
        renderCell: ({ row: { visitor, displayFinancialStatus } }) => {
          const hasRefund = displayFinancialStatus === 'REFUNDED'

          return visitor ? (
            <Chip
              sx={{ textTransform: 'capitalize' }}
              label={
                <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center' }}>
                  {hasRefund ? (
                    <>
                      {!visitor.hasRefund ? (
                        <Tooltip title="The visitor entry is not refunded but the shopify entry is refunded. Go and update the visitor entry.">
                          <SvgIcon fontSize="small" color="error">
                            <IconAlertCircle />
                          </SvgIcon>
                        </Tooltip>
                      ) : null}
                      <TextLink
                        to={editVisitorPath(visitor.id)}
                        label={`Refunded - ${visitor.id}`}
                      />
                    </>
                  ) : (
                    <TextLink
                      to={editVisitorPath(visitor.id)}
                      label={` Fulfilled - ${visitor.id}`}
                    />
                  )}
                </Stack>
              }
              size="small"
              color={hasRefund ? 'error' : 'success'}
            />
          ) : (
            <Chip
              sx={{ textTransform: 'capitalize' }}
              label={hasRefund ? 'Refunded • Missing Visitor' : 'To review'}
              size="small"
              color={hasRefund ? 'error' : 'warning'}
            />
          )
        },
      },
      {
        field: 'shippingAddress',
        headerName: 'Shipping Address',
        width: 200,
        renderCell: ({ row: { shippingAddress } }) => {
          const { firstName, lastName, city, country, zip, address1 } = shippingAddress ?? {}
          return shippingAddress ? (
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              sx={{
                height: 1,
                alignItems: 'center',
                flexShrink: 0,
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="body2">{country}</Typography>
              <CopyToClipboardIconButton
                tooltip="Copy full address"
                content={`${firstName} ${lastName} \n ${address1} \n ${zip} \n ${city} \n ${country}`}
                size="small"
              />
            </Stack>
          ) : (
            '-'
          )
        },
      },
      {
        field: 'id',
        headerName: 'Shopify order',
        renderCell: (params) => (
          <TextLink
            to={createShopifyOrderUrl(params.row.id)}
            label="Shopify order"
            target="_blank"
          />
        ),
        width: 150,
      },
      {
        headerName: 'Email',
        field: 'email',
        flex: 1,
      },
      {
        headerName: 'Sales amount',
        field: 'sales',
        flex: 1,
      },
      {
        headerName: 'Models',
        field: 'products',
        flex: 1,
      },
      {
        resizable: false,
        width: 120,
        field: 'actions',
        type: 'actions',
        getActions: (params) => {
          return [
            <OrderListMenu
              key={params.id}
              onCreateVisitor={() => setVisitorCandidate(params.row.id)}
              disabled={!!params.row.visitor}
            />,
          ]
        },
      },
    ],
    [setVisitorCandidate]
  )

  return { columns }
}
